import { useMemo, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Check, ChevronDown, Search } from 'lucide-react';
import { cn } from '../lib/utils';

// Master selector with a search field: type part of a name or disk number, Enter picks
// the first match. Replaces a flat dropdown that becomes unusable with hundreds of masters.
export function MasterPicker({ id, masters, value, onChange, invalidProps = {}, placeholder = 'Sélectionner un master' }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const selected = masters.find(m => m.id === value);

    const matches = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return masters;
        return masters.filter(m =>
            m.name.toLowerCase().includes(q) || (m.disk_number || '').toLowerCase().includes(q)
        );
    }, [masters, query]);

    const choose = (master) => {
        onChange(master.id);
        setOpen(false);
        setQuery('');
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    id={id}
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    {...invalidProps}
                    className="w-full justify-between font-normal aria-[invalid=true]:border-destructive-text"
                    data-testid="master-select"
                >
                    {selected ? (
                        <span className="font-mono text-sm truncate">{selected.name}</span>
                    ) : (
                        <span className="text-muted-foreground">{placeholder}</span>
                    )}
                    <ChevronDown className="w-4 h-4 opacity-50 shrink-0" aria-hidden="true" />
                </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] p-2">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" aria-hidden="true" />
                    <Input
                        autoFocus
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && matches[0]) {
                                e.preventDefault();
                                choose(matches[0]);
                            }
                        }}
                        placeholder="Nom ou n° de disque…"
                        aria-label="Rechercher un master"
                        className="pl-9"
                    />
                </div>
                <p className="sr-only" aria-live="polite">
                    {matches.length > 1 ? `${matches.length} masters` : `${matches.length} master`}
                </p>
                <ul className="mt-2 max-h-72 overflow-y-auto scrollbar-thin" aria-label="Masters">
                    {matches.length === 0 && (
                        <li className="px-2 py-3 text-sm text-muted-foreground">Aucun master ne correspond.</li>
                    )}
                    {matches.map(master => (
                        <li key={master.id}>
                            <button
                                type="button"
                                onClick={() => choose(master)}
                                aria-pressed={master.id === value}
                                className={cn(
                                    'flex w-full items-start gap-2 rounded-sm px-2 py-2 touch:py-3 text-left hover:bg-secondary focus-visible:outline-none focus-visible:bg-secondary',
                                    master.id === value && 'text-primary'
                                )}
                            >
                                <Check className={cn('w-4 h-4 mt-0.5 shrink-0', master.id === value ? 'opacity-100' : 'opacity-0')} aria-hidden="true" />
                                <span className="min-w-0">
                                    <span className="block font-mono text-sm break-all">{master.name}</span>
                                    {master.disk_number && (
                                        <span className="block text-xs text-muted-foreground font-mono">{master.disk_number}</span>
                                    )}
                                </span>
                            </button>
                        </li>
                    ))}
                </ul>
            </PopoverContent>
        </Popover>
    );
}
